/**
 * CSP Product Page Carousel
 *
 * Handles product image carousel with:
 * - Native horizontal scroll (scroll-snap)
 * - Pagination indicators
 * - Touch navigation
 * - Keyboard navigation
 */

(function () {
  "use strict";

  class CSPCarousel {
    constructor(element) {
      this.carousel = element;
      this.track = element.querySelector("[data-carousel-track]");
      this.dots = element.querySelectorAll("[data-carousel-dot]");
      this.prevBtn = element.querySelector("[data-carousel-prev]");
      this.nextBtn = element.querySelector("[data-carousel-next]");
      this.slides = element.querySelectorAll(".csp-carousel__slide");
      this.currentIndex = 0;

      if (!this.track || this.slides.length === 0) {
        // Don't initialize if required elements are missing
        return;
      }

      this.init();
    }

    init() {
      if (!this.track || this.slides.length === 0) return;
      
      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToPrev();
        });
      }
      
      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToNext();
        });
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToSlide(index);
        });
      });

      this.observeScroll();
      this.carousel.addEventListener("keydown", (e) => this.handleKeyboard(e));
      
      // Initial indicator update
      this.updateIndicators(0);
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      if (!this.track || !this.slides[index]) return;
      
      const slide = this.slides[index];
      const slideLeft = slide.offsetLeft;
      
      this.track.scrollTo({
        left: slideLeft,
        behavior: "smooth",
      });
      
      this.updateIndicators(index);
    }

    goToPrev() {
      const newIndex =
        this.currentIndex > 0 ? this.currentIndex - 1 : this.slides.length - 1;
      this.goToSlide(newIndex);
    }

    goToNext() {
      const newIndex =
        this.currentIndex < this.slides.length - 1 ? this.currentIndex + 1 : 0;
      this.goToSlide(newIndex);
    }

    observeScroll() {
      if (!this.track) return;
      
      // Use scroll event as more reliable than IntersectionObserver for scroll containers
      this.track.addEventListener('scroll', () => {
        this.updateIndicatorsFromScroll();
      }, { passive: true });
      
      // Initial update
      this.updateIndicatorsFromScroll();
    }
    
    updateIndicatorsFromScroll() {
      if (!this.track || this.slides.length === 0) return;
      
      const scrollLeft = this.track.scrollLeft;
      const trackWidth = this.track.clientWidth;
      
      // Find which slide is most visible
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      this.slides.forEach((slide, index) => {
        const slideLeft = slide.offsetLeft;
        const slideCenter = slideLeft + slide.offsetWidth / 2;
        const trackCenter = scrollLeft + trackWidth / 2;
        const distance = Math.abs(slideCenter - trackCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      this.updateIndicators(closestIndex);
    }

    updateIndicators(index) {
      this.currentIndex = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle("csp-carousel__dot--active", i === index);
      });
    }

    handleKeyboard(e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.goToPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        this.goToNext();
      }
    }
  }

  function initCarousels() {
    document.querySelectorAll("[data-carousel]").forEach((carouselElement) => {
      // Skip if already initialized
      if (carouselElement.carouselInstance) return;
      
      const carouselInstance = new CSPCarousel(carouselElement);
      if (carouselInstance.track) { // Only store if initialization succeeded
        carouselElement.carouselInstance = carouselInstance;
      }
    });
  }

  // Initialize immediately if DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCarousels);
  } else {
    // Small delay to ensure all elements are rendered
    setTimeout(initCarousels, 50);
  }
})();
